import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';
import { Layout } from '@/components/layout/Layout';
import { OrganizerLayout } from '@/components/layout/OrganizerLayout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { OrganizerRoute } from '@/components/auth/OrganizerRoute';
import { CompetitionRoute } from '@/components/auth/CompetitionRoute';
import { PublicHomePage } from '@/pages/PublicHomePage';
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { ImportPage } from '@/pages/ImportPage';
import { SchoolsPage } from '@/pages/SchoolsPage';
import { ParticipantsPage } from '@/pages/ParticipantsPage';
import { LeaderboardPage } from '@/pages/LeaderboardPage';
import { MagicLinksPage } from '@/pages/MagicLinksPage';
import { QualificationPage } from '@/pages/QualificationPage';
import { LivePage } from '@/pages/LivePage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { ADMIN_BASE, ADMIN_LOGIN, ORGANIZER_BASE, ORGANIZER_LOGIN } from '@/config/routes';
import { OrganizerLoginPage } from '@/pages/OrganizerLoginPage';
import { PracticalScoresPage } from '@/pages/PracticalScoresPage';
import { SessionStatusPage } from '@/pages/SessionStatusPage';
import { ReportsPage } from '@/pages/ReportsPage';
import { OrganizerHomePage } from '@/pages/OrganizerHomePage';
// Competition (student-facing) pages – public, no ProtectedRoute
import { WeryfikacjaPage } from '@/pages/WeryfikacjaPage';
import { PoczekalniaPage } from '@/pages/PoczekalniaPage';
import { TurniejPage } from '@/pages/TurniejPage';
import { PodziekowaniePage } from '@/pages/PodziekowaniePage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2,
      retry: (failureCount, error) => {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 401 || status === 403) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Public homepage */}
          <Route path="/" element={<PublicHomePage />} />

          {/* Admin login – public but under UUID path */}
          <Route path={ADMIN_LOGIN} element={<LoginPage />} />

          {/* ── Competition flow – student-facing ── */}
          {/* /weryfikacja is fully public – the student arrives here from the magic link */}
          <Route path="/weryfikacja" element={<WeryfikacjaPage />} />
          {/* /poczekalnia and /turniej require a valid session token */}
          <Route element={<CompetitionRoute />}>
            <Route path="/poczekalnia" element={<PoczekalniaPage />} />
            <Route path="/turniej" element={<TurniejPage />} />
          </Route>
          <Route path="/podziekowanie" element={<PodziekowaniePage />} />

          {/* Protected admin panel */}
          <Route path={ADMIN_BASE} element={<ProtectedRoute />}>
            <Route element={<Layout />}>
              <Route index element={<DashboardPage />} />
              <Route path="import" element={<ImportPage />} />
              <Route path="schools" element={<SchoolsPage />} />
              <Route path="participants" element={<ParticipantsPage />} />
              <Route path="live" element={<LivePage />} />
              <Route path="leaderboard" element={<LeaderboardPage />} />
              <Route path="magic-links" element={<MagicLinksPage />} />
              <Route path="qualification" element={<QualificationPage />} />
            </Route>
          </Route>

          {/* Organizer panel – read-only + practical score entry */}
          <Route path={ORGANIZER_LOGIN} element={<OrganizerLoginPage />} />
          <Route path={ORGANIZER_BASE} element={<OrganizerRoute />}>
            <Route element={<OrganizerLayout />}>
              <Route index element={<OrganizerHomePage />} />
              <Route path="live" element={<LivePage readOnly />} />
              <Route path="reports" element={<ReportsPage readOnly />} />
              <Route path="leaderboard" element={<LeaderboardPage readOnly />} />
              <Route path="practical-scores" element={<PracticalScoresPage />} />
              <Route path="participants" element={<ParticipantsPage readOnly />} />
              <Route path="session-status" element={<SessionStatusPage />} />
              <Route path="schools" element={<SchoolsPage readOnly />} />
            </Route>
          </Route>

          {/* Redirect old root paths to login */}
          <Route
            path="/import"
            element={<Navigate to={`${ADMIN_BASE}/import`} replace />}
          />
          <Route
            path="/schools"
            element={<Navigate to={`${ADMIN_BASE}/schools`} replace />}
          />
          <Route
            path="/participants"
            element={<Navigate to={`${ADMIN_BASE}/participants`} replace />}
          />
          <Route
            path="/results"
            element={<Navigate to={`${ADMIN_BASE}/leaderboard`} replace />}
          />
          <Route
            path="/leaderboard"
            element={<Navigate to={`${ADMIN_BASE}/leaderboard`} replace />}
          />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: { background: '#1f2937', color: '#f9fafb' },
          success: { iconTheme: { primary: '#22c55e', secondary: '#f9fafb' } },
          error: { iconTheme: { primary: '#ef4444', secondary: '#f9fafb' } },
        }}
      />
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
